// ── Code-keyed aliases (for country objects with .code + .name) ──────────────
const COUNTRY_ALIASES = {
  US: ["usa", "u.s.a", "u.s.", "america", "united states of america", "united states"],
  GB: ["uk", "u.k.", "britain", "great britain", "england"],
  AE: ["uae", "u.a.e.", "emirates", "dubai"],
  SA: ["ksa", "kingdom of saudi arabia", "saudi"],
  KR: ["korea", "rok", "republic of korea", "south korea"],
  CZ: ["czechia", "czech republic"],
  TR: ["turkiye", "turkey"],
  RU: ["russian federation", "rus"],
  AU: ["aus", "aussie"],
  CA: ["can"],
  NZ: ["nz", "newzealand"],
  JP: ["jpn", "jp"],
  DE: ["ger", "deutschland"],
  FR: ["fra"],
  NL: ["neth", "holland"],
  IT: ["ita"],
  ES: ["spa", "espana"],
  PT: ["por"],
  IE: ["eire"],
  CH: ["swiss", "switz"],
  SG: ["sing"],
  MY: ["mal"],
  TH: ["thai"],
  VN: ["viet", "vn"],
  LK: ["sri lanka", "srilanka"],
  IN: ["ind", "bharat"],
  CN: ["chn", "prc"],
  PK: ["pak"],
  BD: ["bgd"],
  NG: ["ngr"],
  ZA: ["rsa", "south africa"],
  KE: ["ken"],
  EG: ["egy"],
  MX: ["mex"],
  BR: ["bra"],
  AR: ["arg"],
  ID: ["indo", "indonesia"],
  PH: ["phil", "philippines"],
  MV: ["maldives"],
};

// ── Name-keyed aliases (for plain string country names) ───────────────────────
const COUNTRY_NAME_ALIASES = {
  "united states": ["usa", "us", "america", "united states of america"],
  "united states of america": ["usa", "us", "america", "united states"],
  "united kingdom": ["uk", "gb", "britain", "england", "great britain"],
  "united arab emirates": ["uae", "dubai", "emirates"],
  "saudi arabia": ["ksa", "saudi"],
  "south korea": ["korea", "kr", "rok"],
  "czech republic": ["cz", "czechia"],
  "turkey": ["tr", "turkiye"],
  "russia": ["ru", "rus", "russian federation"],
  "australia": ["aus", "au", "aussie"],
  "canada": ["ca", "can"],
  "new zealand": ["nz"],
  "japan": ["jp", "jpn"],
  "germany": ["de", "ger", "deutsch"],
  "france": ["fr", "fra"],
  "netherlands": ["nl", "neth", "holland"],
  "italy": ["it", "ita"],
  "spain": ["es", "spa"],
  "portugal": ["pt", "por"],
  "ireland": ["ie", "eire"],
  "switzerland": ["ch", "swiss", "switz"],
  "singapore": ["sg", "sing"],
  "malaysia": ["my", "mal"],
  "thailand": ["th", "thai"],
  "vietnam": ["vn", "viet"],
  "sri lanka": ["lk"],
  "india": ["in", "ind", "bharat"],
  "china": ["cn", "chn", "prc"],
  "pakistan": ["pk", "pak"],
  "bangladesh": ["bd", "bgd"],
  "nigeria": ["ng", "ngr"],
  "south africa": ["za", "rsa"],
  "kenya": ["ke", "ken"],
  "egypt": ["eg", "egy"],
  "mexico": ["mx", "mex"],
  "brazil": ["br", "bra"],
  "argentina": ["ar", "arg"],
  "indonesia": ["id", "indo"],
  "philippines": ["ph", "phil"],
  "maldives": ["mv"],
  "uae": ["uae", "dubai", "emirates", "united arab emirates"],
};

function normalizeCountryText(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function compactCountryText(value) {
  return normalizeCountryText(value).replace(/\s+/g, "");
}

function getAliasList(country) {
  const code = String(country?.code || "").toUpperCase();
  const name = String(country?.name || "");
  return [
    name,
    code,
    compactCountryText(name),
    ...(COUNTRY_ALIASES[code] || []),
  ].filter(Boolean);
}

// ── For country objects { code, name } (used in Requirements dropdown) ────────
export function matchesCountrySearch(country, query) {
  const normalizedQuery = normalizeCountryText(query);
  if (!normalizedQuery) return true;
  const compactQuery = normalizedQuery.replace(/\s+/g, "");
  return getAliasList(country).some((term) => {
    const normalizedTerm = normalizeCountryText(term);
    const compactTerm = compactCountryText(term);
    return normalizedTerm.includes(normalizedQuery) || compactTerm.includes(compactQuery);
  });
}

// ── For plain country name strings (used in Profile, Applications, Documents) ─
export function matchesCountryString(countryName, query) {
  if (!query) return true;
  const name = normalizeCountryText(countryName);
  const q = normalizeCountryText(query);
  if (!q) return true;
  const compactQ = q.replace(/\s+/g, "");
  const compactName = name.replace(/\s+/g, "");
  if (name.includes(q) || compactName.includes(compactQ)) return true;
  const ownAliases = COUNTRY_NAME_ALIASES[name] || [];
  if (ownAliases.some((a) => a === q || a.startsWith(q))) return true;
  for (const [fullName, aliases] of Object.entries(COUNTRY_NAME_ALIASES)) {
    if (aliases.some((a) => a === q || a.startsWith(q))) {
      const normFull = normalizeCountryText(fullName);
      if (name.includes(normFull) || normFull.includes(name)) return true;
    }
  }
  return false;
}


//   SPEEDOMETER CONSTANTS — speedo.js
//   Navy Blue Gradient Family
// ═══════════════════════════════════════════════════════════════
 
// SVG geometry
export const SPEEDO_CX = 200;           // center X
export const SPEEDO_CY = 200;           // center Y (arc origin)
export const SPEEDO_R = 150;            // arc radius
export const SPEEDO_START_ANGLE = -135; // degrees — left end
export const SPEEDO_END_ANGLE = -45;    // degrees — right end (total sweep = 270°)
export const SPEEDO_SWEEP = 270;        // total degrees of sweep
 
// Arc stroke
export const SPEEDO_STROKE_WIDTH = 18;
export const SPEEDO_TRACK_WIDTH = 18;
 
// Tick geometry
export const SPEEDO_MAJOR_TICK_COUNT = 11; // 0,10,20,…100
export const SPEEDO_MINOR_TICK_COUNT = 50; // 5 minor per major
export const SPEEDO_MAJOR_TICK_LEN = 14;
export const SPEEDO_MINOR_TICK_LEN = 7;
export const SPEEDO_LABEL_OFFSET = 30;     // px beyond arc for numbers
 
// The target value to animate to
export const SPEEDO_DEFAULT_VALUE = 80;    // percent
 
// Animation timing (ms)
export const SPEEDO_ANIM_DELAY = 300;      // delay before needle starts moving
 
// Gradient stop colours — navy blue family
export const SPEEDO_GRADIENT_STOPS = [
  { offset: "0%",   color: "#0a1952" },   // deep navy
  { offset: "25%",  color: "#0d2d87" },   // navy
  { offset: "50%",  color: "#1a4fc4" },   // royal blue
  { offset: "75%",  color: "#3a7bd5" },   // medium blue
  { offset: "100%", color: "#6db3f2" },   // light sky blue
];
 
// Zone coloured bands on the arc (navy-family tints)
export const SPEEDO_ZONES = [
  { from: 0,  to: 33,  color: "rgba(30,  60, 180, 0.55)",  id: "zone-low"  },
  { from: 33, to: 66,  color: "rgba(40, 100, 210, 0.60)",  id: "zone-mid"  },
  { from: 66, to: 100, color: "rgba(80, 160, 255, 0.70)",  id: "zone-high" },
];
 
// Number labels shown on the gauge
export const SPEEDO_TICK_LABELS = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
 
// Zone text labels
export const SPEEDO_ZONE_TEXTS = [
  { label: "Low",    className: "speedo-zone-label--low"  },
  { label: "Medium", className: "speedo-zone-label--mid"  },
  { label: "High",   className: "speedo-zone-label--high" },
];
 
// Helper — convert 0-100 value to needle rotation degrees
// Gauge runs from SPEEDO_START_ANGLE to SPEEDO_START_ANGLE + SPEEDO_SWEEP
export function valueToNeedleDeg(value) {
  const clamped = Math.min(100, Math.max(0, value));
  return SPEEDO_START_ANGLE + (clamped / 100) * SPEEDO_SWEEP;
}
 
// Helper — polar  cartesian (cx, cy are the SVG center)
export function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: cx + r * Math.cos(rad),
    y: cy + r * Math.sin(rad),
  };
}
 
// Helper — build SVG arc path string for a segment
export function arcPath(cx, cy, r, startAngle, endAngle) {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end   = polarToCartesian(cx, cy, r, startAngle);
  const large = endAngle - startAngle <= 180 ? "0" : "1";
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${large} 0 ${end.x} ${end.y}`;
}
 
// Helper — angle for a given percent value on the gauge
export function valueToAngle(value) {
  // gauge: -135 deg (0%)  +135 deg (100%)  i.e. start=-135, sweep=270
  return SPEEDO_START_ANGLE + (value / 100) * SPEEDO_SWEEP;
}